import { useMemo, useCallback, useReducer, useEffect } from 'react';
import { supabase, JobStatus, useRealtimeSubscription, JobEventType, JobWithRelations } from '@cleanpro/common-ui';
import type { Quote, Json } from '@cleanpro/common-ui';
import { Job, BlockedDate, JobRow, BlockedDateRow, JobEvent } from '../types';
import { addJob as apiAddJob, JobInsertData } from '../api';
import { JobsStateContext, JobsDispatchContext } from './JobsContextObject';
import { useJobsData } from '../hooks/useJobsData';
import type { Business } from '../../service-providers/business';
import type { Service } from '../../services/types';
import type { Property } from '../../properties/types';
import type { CustomerResponse as Customer } from '../../customers/types';

interface JobsStateInternal {
  jobs: JobWithRelations[];
  loading: boolean;
  error: string | null;
  blockedDates: BlockedDate[];
  customers: Customer[];
  services: Service[];
  businesses: Business[];
  properties: Property[];
  quotes: Quote[];
  jobEvents: JobEvent[];
}

// Util to transform blocked date row
const transformBlockedDate = (row: BlockedDateRow): BlockedDate => ({
  id: row.id,
  date: row.date,
  reason: row.reason || undefined,
  createdBy: row.created_by || undefined,
  createdAt: row.created_at || '',
  updatedAt: row.updated_at || ''
});

// Action types for the reducer
type JobsAction =
  | { type: 'SET_JOBS'; payload: Job[] }
  | { type: 'ADD_JOB'; payload: Job }
  | { type: 'UPDATE_JOB'; payload: Job }
  | { type: 'REMOVE_JOB'; payload: string }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_BLOCKED_DATES'; payload: BlockedDate[] }
  | { type: 'ADD_BLOCKED_DATE'; payload: BlockedDate }
  | { type: 'REMOVE_BLOCKED_DATE'; payload: string }
  | { type: 'UPDATE_BLOCKED_DATE'; payload: BlockedDate }
  | { type: 'SET_CUSTOMERS'; payload: Customer[] }
  | { type: 'SET_SERVICES'; payload: Service[] }
  | { type: 'SET_BUSINESSES'; payload: Business[] }
  | { type: 'SET_JOB_EVENTS'; payload: JobEvent[] }
  | { type: 'ADD_JOB_EVENT'; payload: JobEvent }
  | { type: 'SET_PROPERTIES'; payload: Property[] }
  | { type: 'SET_QUOTES'; payload: Quote[] };

// Reducer function following Kent C. Dodds' pattern
function jobsReducer(state: JobsStateInternal, action: JobsAction): JobsStateInternal {
  switch (action.type) {
    case 'SET_JOBS':
      return { ...state, jobs: action.payload };
    case 'ADD_JOB': {
      const exists = state.jobs.some((j: Job) => j.id === action.payload.id);
      if (exists) return state;
      return { ...state, jobs: [...state.jobs, action.payload] };
    }
    case 'UPDATE_JOB':
      return {
        ...state,
        jobs: state.jobs.map((job: Job) =>
          job.id === action.payload.id ? action.payload : job
        ),
      };
    case 'REMOVE_JOB':
      return {
        ...state,
        jobs: state.jobs.filter((job: Job) => job.id !== action.payload),
      };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_BLOCKED_DATES':
      return { ...state, blockedDates: action.payload };
    case 'ADD_BLOCKED_DATE': {
      const exists = state.blockedDates.some((bd: BlockedDate) => bd.id === action.payload.id);
      if (exists) return state;
      return {
        ...state,
        blockedDates: [...state.blockedDates, action.payload].sort((a, b) =>
          new Date(a.date).getTime() - new Date(b.date).getTime()
        ),
      };
    }
    case 'REMOVE_BLOCKED_DATE':
      return {
        ...state,
        blockedDates: state.blockedDates.filter((bd: BlockedDate) => bd.id !== action.payload),
      };
    case 'UPDATE_BLOCKED_DATE':
      return {
        ...state,
        blockedDates: state.blockedDates.map((bd: BlockedDate) =>
          bd.id === action.payload.id ? action.payload : bd
        ),
      };
    case 'SET_CUSTOMERS':
      return { ...state, customers: action.payload };
    case 'SET_SERVICES':
      return { ...state, services: action.payload };
    case 'SET_BUSINESSES':
      return { ...state, businesses: action.payload };
    case 'SET_JOB_EVENTS':
      return { ...state, jobEvents: action.payload };
    case 'SET_PROPERTIES':
      return { ...state, properties: action.payload };
    case 'SET_QUOTES':
      return { ...state, quotes: action.payload };
    case 'ADD_JOB_EVENT': {
      const exists = state.jobEvents.some((e: JobEvent) => e.id === action.payload.id);
      if (exists) return state;
      return { ...state, jobEvents: [...state.jobEvents, action.payload] };
    }
    default:
      throw new Error(`Unhandled action type: ${(action as JobsAction).type}`);
  }
}

const getChangedFields = (currentJob: Job, updateData: Record<string, unknown>) => {
  const changedFields: Record<string, unknown> = {};
  for (const key in updateData) {
    if (Object.prototype.hasOwnProperty.call(updateData, key)) {
      const jobKey = key.replace(/_([a-z])/g, (g) => g[1].toUpperCase()) as keyof Job;
      if (currentJob[jobKey] !== updateData[key]) {
        changedFields[key] = updateData[key];
      }
    }
  }
  return changedFields;
};

const createUpdateDescription = (_changedFields: Record<string, unknown>, _customers: Customer[], _services: Service[]) => {
  return Object.keys(_changedFields).map(key => `${key} updated`).join(', ');
};

const generateInvoiceFromJob = async (job: JobWithRelations) => {
  try {
    console.log('Generating invoice for job:', job.id);
    
    // Validate required data
    if (!job.customer?.id) {
      throw new Error('Customer information missing for invoice generation');
    }
    
    // Fetch the associated quote to get line items and pricing details
    let lineItems: Json = [];
    let subtotal = job.price;
    let taxAmount = 0;
    let taxRate = 0;
    let discountAmount = 0;
    let quoteId: string | undefined;

    // Get the quote_id from the jobs table
    const { data: jobData, error: jobError } = await supabase
      .from('jobs')
      .select('quote_id')
      .eq('id', job.id)
      .single();

    if (jobError) {
      console.error('Error fetching job quote_id:', jobError);
    } else if (jobData?.quote_id) {
      quoteId = jobData.quote_id;
      
      // Fetch the quote with its line items
      const { data: quoteData, error: quoteError } = await supabase
        .from('quotes')
        .select('line_items, subtotal, tax_amount, tax_rate, discount_amount')
        .eq('id', quoteId)
        .single();

      if (quoteError) {
        console.error('Error fetching quote:', quoteError);
      } else if (quoteData && quoteData.line_items) {
        // Use quote's line items and pricing directly (they're already in Json format)
        lineItems = quoteData.line_items;
        subtotal = quoteData.subtotal || job.price;
        taxAmount = quoteData.tax_amount || 0;
        taxRate = quoteData.tax_rate || 0;
        discountAmount = quoteData.discount_amount || 0;
        
        console.log('Using line items from quote:', lineItems);
      }
    }

    // If no quote or no line items, create a simple line item
    if (!lineItems || (Array.isArray(lineItems) && lineItems.length === 0)) {
      lineItems = [{
        description: job.service?.name || 'Cleaning Service',
        quantity: 1,
        unitPrice: job.price,
        total: job.price
      }];
      subtotal = job.price;
      taxAmount = 0;
      taxRate = 0;
      discountAmount = 0;
    }
    
    // Generate invoice number with format: INV-YYYYMMDD-XXXX
    const date = new Date();
    const dateStr = date.toISOString().slice(0, 10).replace(/-/g, '');
    const randomNum = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    const invoiceNumber = `INV-${dateStr}-${randomNum}`;
    
    // Insert invoice record
    const { data, error } = await supabase
      .from('invoices')
      .insert({
        invoice_number: invoiceNumber,
        customer_id: job.customer.id,
        job_id: job.id,
        quote_id: quoteId || null,
        amount: job.price,
        subtotal: subtotal,
        tax_amount: taxAmount,
        tax_rate: taxRate,
        discount_amount: discountAmount,
        status: 'draft',
        line_items: lineItems,
        issue_date: new Date().toISOString().slice(0, 10),
        due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
      })
      .select()
      .single();
    
    if (error) {
      console.error('Error creating invoice:', error);
      throw error;
    }
    
    console.log('Invoice created successfully:', data);
  } catch (err) {
    console.error('Error in generateInvoiceFromJob:', err);
    throw err;
  }
};

/**
 * Provides job data and related actions to its children via React Context.
 * 
 * Refactored following Kent C. Dodds' best practices:
 * - Uses useReducer for predictable state management
 * - Separates state and dispatch contexts to prevent unnecessary re-renders
 * - Extracts data fetching logic to a custom hook
 * - Dispatch function is automatically stable (no need for useCallback)
 *
 * @param {Object} props - The props for the provider
 * @param {React.ReactNode} props.children - The child components to be rendered
 * @returns {JSX.Element} The context provider wrapping its children
 */
export const JobsDataProvider = ({ 
  children
}: { children: React.ReactNode }) => {
  // Use custom hook for data fetching
  const {
    jobs: jobRows,
    loading: dataLoading,
    error: dataError,
    blockedDates: dataBlockedDates,
    customers: dataCustomers,
    services: dataServices,
    businesses: dataBusinesses,
    properties: dataProperties
  } = useJobsData();

  // Initialize reducer with data from useJobsData
  const [state, dispatch] = useReducer(jobsReducer, {
    jobs: [],
    loading: dataLoading,
    error: dataError,
    blockedDates: dataBlockedDates,
    customers: dataCustomers,
    services: dataServices,
    businesses: dataBusinesses,
    properties: dataProperties,
    quotes: [],
    jobEvents: [],
  });

  // Helper function to transform job rows to Job objects
  const transformJob = useCallback((
    row: JobRow, 
    customersData: Customer[],
    propertyData: Property[],
    servicesData: Service[],
    businessData: Business[]
  ): JobWithRelations => {
    const customer = customersData.find(c => c.id === row.customer_id);
    const property = propertyData.find(p => p.id === row.property_id);
    const service = servicesData.find(s => s.id === row.service_id);
    const business = businessData.find(b => b.id === row.business_id);

    return {
      id: row.id,
      status: row.status as JobStatus,
      price: row.price,
      notes: row.notes || '',
      date_time: row.date_time || row.date || '',
      duration: row.duration,
      isRecurring: row.is_recurring || false,
      recurringFrequency: row.recurring_frequency || undefined,
      recurringEndDate: row.recurring_end_date || undefined,
      recurringParentId: row.recurring_parent_id || undefined,
      createdAt: '',
      updatedAt: '',
      customer: customer ? {
        id: customer.id,
        name: customer.name,
        email: customer.email,
        phone: customer.phone,
        address: customer.address,
      } : undefined,
      property: property ? {
        id: property.id,
        name: property.name || '',
        customerId: property.customerId,
        propertyType: property.propertyType,
        addressLine1: property.addressLine1,
        addressLine2: property.addressLine2,
        city: property.city,
        state: property.state,
        postalCode: property.postalCode,
        squareFootage: property.squareFootage,
        bedrooms: property.bedrooms,
        bathrooms: property.bathrooms,
        floors: property.floors,
        accessInstructions: property.accessInstructions,
        specialInstructions: property.specialInstructions,
        hasPets: property.hasPets,
        petDetails: property.petDetails,
        preferredCleaningDay: property.preferredCleaningDay,
        requestedTimeSlot: property.requestedTimeSlot,
        isActive: property.isActive,
        createdAt: property.createdAt,
        updatedAt: property.updatedAt
      } : undefined,
      service: service ? {
        id: service.id,
        name: service.name,
        description: service.description,
        category: service.category,
        duration: service.duration || row.duration,
        basePrice: service.basePrice || 0,
        addOns: service.addOns || [],
        createdAt: service.createdAt || '',
        updatedAt: service.updatedAt || ''
      } : undefined,
      business: business ? {
        id: business.id,
        businessName: business.businessName || 'Unassigned',
        email: business.email || '',
        phone: business.phone || '',
        ownerName: business.ownerName || '',
        status: business.status || 'inactive',
        contractStartDate: business.contractStartDate || '',
        hourlyRate: business.hourlyRate || 0,
        availability: business.availability || {
          monday: false,
          tuesday: false,
          wednesday: false,
          thursday: false,
          friday: false,
          saturday: false,
          sunday: false
        },
        address: business.address || {
          addressLine1: '',
          addressLine2: '',
          addressCity: '',
          addressState: '',
          addressPostalCode: ''
        },
        businessType: business.businessType || 'independent_contractor',
        servicesOffered: business.servicesOffered || [],
        rating: business.rating || 0,
        completedJobs: business.completedJobs || 0,
        createdAt: business.createdAt || '',
        updatedAt: business.updatedAt || ''
      } : undefined
    };
  }, []);

  // Sync data from useJobsData to reducer state
  useEffect(() => {
    const transformedJobs = jobRows.map(job => 
      transformJob(job, dataCustomers, dataProperties, dataServices, dataBusinesses )
    );
    dispatch({ type: 'SET_JOBS', payload: transformedJobs });
    dispatch({ type: 'SET_LOADING', payload: dataLoading });
    dispatch({ type: 'SET_ERROR', payload: dataError });
    dispatch({ type: 'SET_BLOCKED_DATES', payload: dataBlockedDates });
    dispatch({ type: 'SET_CUSTOMERS', payload: dataCustomers });
    dispatch({ type: 'SET_SERVICES', payload: dataServices });
    dispatch({ type: 'SET_BUSINESSES', payload: dataBusinesses });
    dispatch({ type: 'SET_PROPERTIES', payload: dataProperties });
    dispatch({ type: 'SET_QUOTES', payload: [] });
  }, [jobRows, dataCustomers, dataServices, dataBusinesses, dataProperties, dataLoading, dataError, dataBlockedDates, transformJob]);

  // Setup real-time subscription for jobs (filter to active jobs only)
  useRealtimeSubscription({
    table: 'jobs',
    filter: 'status=neq.cancelled',
    callbacks: {
      onInsert: (payload) => {
        const newJob = transformJob(payload.new as JobRow, state.customers, state.properties, state.services, state.businesses);
        dispatch({ type: 'ADD_JOB', payload: newJob });
      },
      onUpdate: (payload) => {
        const updatedJob = transformJob(payload.new as JobRow, state.customers, state.properties, state.services, state.businesses);
        dispatch({ type: 'UPDATE_JOB', payload: updatedJob });
      },
      onDelete: (payload) => {
        const oldRecord = payload.old as { id: string };
        dispatch({ type: 'REMOVE_JOB', payload: oldRecord.id });
      },
    },
  });


  // Get job events for a specific job from state
  const getJobEvents = useCallback((jobId: string): JobEvent[] => {
    return state.jobEvents.filter(event => event.jobId === jobId);
  }, [state.jobEvents]);

  const addBlockedDate = useCallback(async (date: string, reason?: string) => {
    try {
      const { data, error } = await supabase
        .from('blocked_dates')
        .insert({
          date,
          reason: reason || null,
          created_by: (await supabase.auth.getUser()).data.user?.id
        })
        .select()
        .single();

      if (error) throw error;

      const newBlockedDate = transformBlockedDate(data);
      dispatch({ type: 'ADD_BLOCKED_DATE', payload: newBlockedDate });
    } catch (err) {
      console.error('Error adding blocked date:', err);
      throw err;
    }
  }, []);

  const removeBlockedDate = useCallback(async (dateId: string) => {
    try {
      const { error } = await supabase
        .from('blocked_dates')
        .delete()
        .eq('id', dateId);

      if (error) throw error;

      dispatch({ type: 'REMOVE_BLOCKED_DATE', payload: dateId });
    } catch (err) {
      console.error('Error removing blocked date:', err);
      throw err;
    }
  }, []);

  const fetchJobEvents = useCallback(async (): Promise<void> => {
    try {
      console.log('Fetching job events...');
      const { data, error } = await supabase
        .from('job_events')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Transform database rows to JobEvent objects
      const jobEvents: JobEvent[] = (data || []).map(row => ({
        id: row.id,
        jobId: row.job_id,
        eventType: row.event_type as JobEventType,
        description: row.description,
        details: row.details || undefined,
        userName: row.user_name || 'System',
        createdAt: row.created_at || ''
      }));

      dispatch({ type: 'SET_JOB_EVENTS', payload: jobEvents });
      console.log('Job events fetched and stored in state:', jobEvents.length, 'events');
    } catch (err: unknown) {
      console.error('Error fetching job events:', (err as Error).message);
    }
  }, []);

  const addJob = useCallback(async (job: JobInsertData) => {
    console.log('JobsContext: addJob started', job);
    try {
      const newJobData = await apiAddJob(job);
      console.log('JobsContext: Supabase insert successful', newJobData);
      const newJob = transformJob(newJobData as JobRow, state.customers, state.properties, state.services, state.businesses);
      console.log('JobsContext: Transformed new job', newJob);
      dispatch({ type: 'ADD_JOB', payload: newJob });
      console.log('JobsContext: addJob finished');
    } catch (err) {
      console.error('JobsContext: Error in addJob', err);
      throw err;
    }
  }, [state.customers, state.properties, state.services, state.businesses, transformJob]);

  const removeJob = useCallback((jobId: string) => {
    dispatch({ type: 'REMOVE_JOB', payload: jobId });
  }, []);

  const addJobEvent = useCallback(async (event: Omit<JobEvent, 'id' | 'createdAt'>) => {
    try {
      const { data, error } = await supabase
        .from('job_events')
        .insert({
          job_id: event.jobId,
          event_type: event.eventType,
          description: event.description,
          details: event.details || null,
          user_name: event.userName || 'System',
          created_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;
      
      // Add the event to state
      const newEvent: JobEvent = {
        id: data.id,
        jobId: data.job_id,
        eventType: data.event_type as JobEventType,
        description: data.description,
        details: data.details || undefined,
        userName: data.user_name || 'System',
        createdAt: data.created_at || ''
      };
      
      dispatch({ type: 'ADD_JOB_EVENT', payload: newEvent });
      console.log('Job event recorded and added to state:', newEvent);
    } catch (err) {
      console.error('Error recording job event:', err);
      throw err;
    }
  }, []);

  const updateJob = useCallback(async (jobId: string, jobData: Partial<Omit<Job, 'id' | 'customerName' | 'serviceName' | 'businessName'>>) => {
    try {
      // Get the current job data to compare changes
      const currentJob = state.jobs.find(b => b.id === jobId);
      if (!currentJob) {
        throw new Error('Job not found');
      }

      const updateData: Record<string, unknown> = {};
      
      if (jobData.date_time !== undefined) updateData.date_time = jobData.date_time;
      if (jobData.duration !== undefined) updateData.duration = jobData.duration;
      if (jobData.status !== undefined) updateData.status = jobData.status;
      if (jobData.price !== undefined) updateData.price = jobData.price;
      if (jobData.notes !== undefined) updateData.notes = jobData.notes;

      const { data, error } = await supabase
        .from('jobs')
        .update(updateData)
        .eq('id', jobId)
        .select()
        .single();

      if (error) throw error;

      console.log('data', data);

      const { data: refreshedJobData, error: refreshError } = await supabase
        .from('jobs')
        .select('*')
        .eq('id', jobId)
        .single();

      if (refreshError) throw refreshError;

      const updatedJob = transformJob(refreshedJobData as JobRow, state.customers, state.properties, state.services, state.businesses);
      dispatch({ type: 'UPDATE_JOB', payload: updatedJob });

      // Only create an event if there are actual changes
      const changedFields = getChangedFields(currentJob, updateData);
      if (Object.keys(changedFields).length > 0) {
        const updateDescription = createUpdateDescription(changedFields, state.customers, state.services);
        await addJobEvent({
          jobId,
          eventType: 'updated',
          description: updateDescription,
          details: '',
          userName: 'System'
        });
      }
    } catch (err) {
      console.error('Error updating job:', err);
      throw err;
    }
  }, [state.jobs, state.customers, state.properties, state.services, state.businesses, transformJob, addJobEvent]);

  const updateJobStatus = useCallback(async (jobId: string, status: Job['status']) => {
    try {
      const { error } = await supabase
        .from('jobs')
        .update({ status })
        .eq('id', jobId);

      if (error) throw error;

      // Get the current job data for invoice generation
      const currentJob = state.jobs.find(b => b.id === jobId);

      // Update job status in state
      if (currentJob) {
        dispatch({ type: 'UPDATE_JOB', payload: { ...currentJob, status } });
      }

      // Add an event for the status change
      await addJobEvent({
        jobId,
        eventType: 'status_changed',
        description: `Status changed to ${status}`,
        details: `Job status updated to ${status}`,
        userName: 'System'
      });

      // Automatically generate invoice when job is completed
      if (status === 'completed' && currentJob) {
        console.log(`Job ${jobId} completed, generating invoice...`);
        await generateInvoiceFromJob(currentJob);
      }
    } catch (err) {
      console.error('Error updating job status:', err);
      throw err;
    }
  }, [state.jobs, addJobEvent]);

  const validateJobSchedulingCallback = useCallback(async (businessId: string, date: string, time: string, duration: number, jobIdToExclude?: string) => {
    console.log('validateJobScheduling', businessId, date, time, duration, jobIdToExclude);
    return {
      isValid: true,
      conflicts: [],
      suggestions: []
    };
  }, []);

  // Actions object with all action functions
  // NOTE: dispatch is automatically stable from useReducer, no memoization needed
  const actions = useMemo(() => ({
    addJob,
    removeJob,
    updateJob,
    updateJobStatus,
    getJobEvents,
    addJobEvent,
    fetchJobEvents,
    addBlockedDate,
    removeBlockedDate,
    validateJobScheduling: validateJobSchedulingCallback,
    transformBlockedDate,
  }), [
    addJob,
    removeJob,
    updateJob,
    updateJobStatus,
    getJobEvents,
    addJobEvent,
    fetchJobEvents,
    addBlockedDate,
    removeBlockedDate,
    validateJobSchedulingCallback,
  ]);

  return (
    <JobsStateContext.Provider value={state}>
      <JobsDispatchContext.Provider value={actions}>
        {children}
      </JobsDispatchContext.Provider>
    </JobsStateContext.Provider>
  );
};

